import Icon, { QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Tooltip } from 'antd';
import React, { HTMLAttributes } from 'react';
import { useTranslate } from '@tolgee/react';
import { RobotContributionOverviewRes } from 'services/api/dashboard';
import { currencyDisplay } from 'views/Dashboard/ImplementData/utils';
import { ReactComponent as IconTip } from 'views/SBS/Report/icons/tip.svg';

import robotImg from '../../assets/robot.png';

const Container = styled.div`
  width: 100%;
  min-height: 190px;
  position: relative;
  background-image: linear-gradient(
    133deg,
    #ffffff 0%,
    rgba(198, 204, 255, 0.98) 66%,
    rgba(201, 214, 255, 0.95) 100%
  );
  box-shadow: 0 2px 12px 0 rgba(167, 178, 204, 0.1);
  border-radius: 4px;
  padding-left: 140px;
  padding-top: 24px;
  padding-right: 24px;
  padding-bottom: 16px;
  [data-color-mode='dark'] & {
    background-image: linear-gradient(
      133deg,
      var(--fett-bg-elevated) 0%,
      var(--fett-bg-hover) 100%
    );
    .title {
      color: var(--fett-text-primary);
    }
    .titleSmall {
      color: var(--fett-text-secondary);
      background: var(--fett-bg-panel);
    }
  }
  .bg {
    position: absolute;
    top: 0;
    left: 0;
    height: 90%;
  }
  .title {
    color: #192745;
    font-size: 24px;
    position: relative;
    z-index: 1;
  }
  .titleSmall {
    position: relative;
    color: #414e6a;
    background: #ffffffcc;
    border-radius: 4px;
    padding: 0 12px;
    line-height: 32px;
    display: inline-block;
    z-index: 1;
  }
`;
const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;
const Card = styled.div`
  background: var(--fett-bg-panel);
  box-shadow: 0px 2px 12px 0px rgba(167, 178, 204, 0.1);
  width: 150px;
  min-height: 96px;
  margin-top: 16px;
  padding: 16px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  position: relative;
  .label {
    color: var(--fett-text-tertiary);
    svg {
      margin-left: 4px;
    }
  }
  .val {
    color: var(--fett-text-secondary);
    font-size: 24px;
    font-weight: 500;
  }
  .unit {
    color: var(--fett-text-tertiary);
    margin-left: 4px;
  }
`;
const tooltipCls = css`
  white-space: pre-wrap;
`;

interface Props extends HTMLAttributes<HTMLDivElement> {
  value?: RobotContributionOverviewRes;
  showPage?: string;
}
const Banner = (props: Props) => {
  const { value, showPage, ...otherProps } = props;
  const { t } = useTranslate();
  return (
    <Container {...otherProps}>
      <img src={robotImg} alt="bg" className="bg" />
      <div className="title">{t('ViewDashboard.AI智能机器人为您节省的客服人力：')}</div>
      {showPage === 'sbs' ? (
        <div className="titleSmall">
          <Icon component={IconTip} style={{ width: '16px', height: '16px', marginRight: '4px' }} />
          {t(
            'sbs.举例：一般1位人工客服平均一天可以回复150条买家的问题，如果机器人可以回复450条买家的问题，那么机器人的工作量相当于3人天，即3位人工客服一天的工作量。',
          )}
        </div>
      ) : null}
      <Flex>
        <Card>
          <div className="label">
            <span>{t('ViewDashboard.会话接待')}</span>
            <Tooltip title={t('ViewDashboard.会话接待tips')} overlayClassName={tooltipCls}>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div>
            <span className="val">{currencyDisplay(value?.conversationReplyCount)}</span>
            <span className="unit">{t('ViewDashboard.人/天')}</span>
          </div>
        </Card>
        <Card>
          <div className="label">
            <span>{t('ViewDashboard.催下单')}</span>
            <Tooltip title={t('ViewDashboard.催下单tips')} overlayClassName={tooltipCls}>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div>
            <span className="val">
              {value?.reminderOrderCount === -1 ? '-' : currencyDisplay(value?.reminderOrderCount)}
            </span>
            <span className="unit">{t('ViewDashboard.人/天')}</span>
          </div>
        </Card>
        <Card>
          <div className="label">
            <span>{t('ViewDashboard.催付款')}</span>
            <Tooltip title={t('ViewDashboard.催付款tips')} overlayClassName={tooltipCls}>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div>
            <span className="val">
              {value?.reminderPaymentCount === -1
                ? '-'
                : currencyDisplay(value?.reminderPaymentCount)}
            </span>
            <span className="unit">{t('ViewDashboard.人/天')}</span>
          </div>
        </Card>
        <Card>
          <div className="label">
            <span>{t('ViewDashboard.取消订单挽回')}</span>
            <Tooltip title={t('ViewDashboard.取消订单挽回tips')} overlayClassName={tooltipCls}>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div>
            <span className="val">
              {value?.cancelOrderReminderCount === -1
                ? '-'
                : currencyDisplay(value?.cancelOrderReminderCount)}
            </span>
            <span className="unit">{t('ViewDashboard.人/天')}</span>
          </div>
        </Card>
        <Card>
          <div className="label">
            <span>{t('ViewDashboard.催好评')}</span>
            <Tooltip title={t('ViewDashboard.催好评tips')} overlayClassName={tooltipCls}>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div>
            <span className="val">
              {value?.praiseReminderCount === -1
                ? '-'
                : currencyDisplay(value?.praiseReminderCount)}
            </span>
            <span className="unit">{t('ViewDashboard.人/天')}</span>
          </div>
        </Card>
        <Card>
          <div className="label">
            <span>{t('ViewDashboard.自动回评')}</span>
            <Tooltip title={t('ViewDashboard.自动回评tips')} overlayClassName={tooltipCls}>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div>
            <span className="val">
              {value?.autoReviewCount === -1 ? '-' : currencyDisplay(value?.autoReviewCount)}
            </span>
            <span className="unit">{t('ViewDashboard.人/天')}</span>
          </div>
        </Card>
      </Flex>
    </Container>
  );
};

export default Banner;
